import { createContext, useContext, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

/**
 * Minimal portal target for "the action button that lives in the
 * page header on the right". Lets a sub-page (e.g. TicketListPage)
 * own its own action without forcing the layout to know about it,
 * and keeps the page chrome (title, tabs, padding) DRY across every
 * tab in the support shell.
 *
 * Layout side:
 *   const [el, setEl] = useState<HTMLDivElement | null>(null);
 *   <HeaderActionContext.Provider value={el}>
 *     <PageHeader title="…" right={<div ref={setEl} />} />
 *     <Outlet />
 *   </HeaderActionContext.Provider>
 *
 * Page side:
 *   <HeaderAction><MyButton /></HeaderAction>
 */
export const HeaderActionContext = createContext<HTMLDivElement | null>(null);

export function HeaderAction({ children }: { children: ReactNode }): JSX.Element | null {
  const target = useContext(HeaderActionContext);
  if (!target) return null;
  return createPortal(children, target);
}

/** Tiny convenience for layout: returns [providerEl, setProviderEl]. */
export function useHeaderActionTarget() {
  return useState<HTMLDivElement | null>(null);
}
